import {
  HttpInterceptorFn,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';

import { AuthService } from '@services/auth';
import { environment } from '@environment';

/**
 * HTTP Correlation Interceptor
 *
 * Dual responsibilities:
 * 1. Injects an `x-request-id` UUID header on every outgoing Supabase request
 *    (both REST and Edge Function calls share the same `supabaseUrl` base).
 *    Non-Supabase requests (e.g., Auth0) are forwarded unmodified.
 *
 * 2. Captures HTTP errors to Sentry according to filtering rules:
 *    - Always report: status 0 (transport failures), status >= 500 (server errors)
 *    - Report: status 404, 405, 408, 409, 429 (unexpected client errors)
 *    - Filter out: status 400, 401, 403 (expected user/permission failures)
 *
 *    Sentry context includes:
 *    - `http.status_code` — numeric HTTP status
 *    - `http.url`         — pathname only (no query params, no origin)
 *    - `http.method`      — HTTP verb
 *    - `request_id`       — the UUID sent as `x-request-id`
 *    - `error_code`       — backend error code from `error.error.code` (omitted when absent)
 *
 * 3. LEA-257: on Supabase 403 while authenticated, notify AuthService so MainLayout
 *    can recover company context after partial membership deactivation.
 */
export const httpCorrelationInterceptor: HttpInterceptorFn = (req, next) => {
  const requestId = crypto.randomUUID();
  const authService = inject(AuthService);

  const outgoing = isSupabaseRequest(req.url, environment.supabaseUrl)
    ? req.clone({ headers: req.headers.set('x-request-id', requestId) })
    : req;

  return next(outgoing).pipe(
    catchError((error) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === 403 &&
        isSupabaseRequest(req.url, environment.supabaseUrl) &&
        authService.isAuthenticatedSync
      ) {
        authService.notifyCompanyScopedDenial();
      }

      if (error instanceof HttpErrorResponse && shouldReportHttpError(error.status) && Sentry.isInitialized()) {
        let urlPathname = 'unknown';
        try {
          urlPathname = error.url ? new URL(error.url).pathname : 'unknown';
        } catch {
          urlPathname = (error.url ?? '').split('?')[0];
        }

        const context: Record<string, unknown> = {
          'http.status_code': error.status,
          'http.url': urlPathname,
          'http.method': req.method,
          request_id: requestId,
        };

        const errorCode = (error.error as Record<string, unknown> | null)?.['code'];
        if (errorCode !== undefined && errorCode !== null) {
          context['error_code'] = errorCode;
        }

        Sentry.captureException(error, { contexts: { http: context } });
      }

      // Re-throw the error to continue Angular's error handling
      return throwError(() => error);
    })
  );
};

/**
 * Returns true when the given URL targets the Supabase backend.
 * Both REST API and Edge Function URLs share the same `supabaseUrl` base.
 */
function isSupabaseRequest(url: string, supabaseUrl: string): boolean {
  return url.startsWith(supabaseUrl);
}

/**
 * Determines if an HTTP error status code should be reported to Sentry.
 *
 * Rules:
 * - Always report: 0 (transport failures — offline, CORS, DNS, network-layer errors)
 * - Always report: 5xx (all server errors)
 * - Report: 404, 405, 408, 409, 429 (unexpected client errors)
 * - Filter out: 400, 401, 403 (expected user/permission failures)
 *
 * @param statusCode - The HTTP status code
 * @returns true if the error should be reported to Sentry
 */
function shouldReportHttpError(statusCode: number): boolean {
  // Report transport failures (offline, CORS, DNS, network-layer errors)
  if (statusCode === 0) {
    return true;
  }

  // Always report server errors
  if (statusCode >= 500) {
    return true;
  }

  // Report specific client errors that indicate bugs or infrastructure issues
  const unexpectedClientErrors = [404, 405, 408, 409, 429];
  if (unexpectedClientErrors.includes(statusCode)) {
    return true;
  }

  // Filter out expected client errors (user/permission failures, validation)
  // 400: Bad Request (validation error)
  // 401: Unauthorized
  // 403: Forbidden (permission denied)

  return false;
}
